import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  Bot, Plus, GitFork, Pause, Play, Snowflake, Skull, DollarSign, X, Loader2, Hammer, HandCoins, Headset, Server, Zap, Gauge, Sparkles, ArrowRight, Target, Megaphone,
} from "lucide-react";

const ROLE_META = {
  builder: { icon: Hammer, label: "Builder", color: "text-cyan-400" },
  seller: { icon: HandCoins, label: "Seller", color: "text-emerald-400" },
  support: { icon: Headset, label: "Support", color: "text-amber-400" },
  api_operator: { icon: Server, label: "API Operator", color: "text-violet-400" },
  marketer: { icon: Megaphone, label: "Marketer", color: "text-sky-400" },
};

const CATEGORIES = ["domain_purchase", "deployment", "content_generation", "api_credits", "ad_spend", "outreach", "refund"];

const TEMPLATES = [
  { key: "lead-hunter", name: "Lead Hunter", role: "seller", icon: Target, accent: "#10B981",
    tagline: "Finds & qualifies inbound leads, hands hot prospects to a closer.",
    mission: "Find and qualify inbound leads, then route hot prospects to a closer for follow-up.",
    wallet_budget: 150, daily_cap: 75, monthly_cap: 600 },
  { key: "saas-closer", name: "SaaS Closer", role: "seller", icon: Megaphone, accent: "#34D399",
    tagline: "Works qualified leads through to a paid Stripe checkout.",
    mission: "Nurture qualified leads and close them through to a paid Stripe checkout.",
    wallet_budget: 250, daily_cap: 120, monthly_cap: 1000 },
  { key: "site-builder", name: "Site Builder", role: "builder", icon: Hammer, accent: "#06B6D4",
    tagline: "Generates & ships conversion-focused sites to a live subdomain.",
    mission: "Generate and deploy conversion-focused landing pages to a live branded subdomain.",
    wallet_budget: 200, daily_cap: 100, monthly_cap: 800 },
  { key: "support-concierge", name: "Support Concierge", role: "support", icon: Headset, accent: "#F59E0B",
    tagline: "Answers customer questions and resolves tickets 24/7.",
    mission: "Answer customer questions and resolve support tickets around the clock.",
    wallet_budget: 100, daily_cap: 50, monthly_cap: 400 },
  { key: "api-vendor", name: "API Vendor", role: "api_operator", icon: Server, accent: "#8B5CF6",
    tagline: "Packages data into metered API endpoints and manages credits.",
    mission: "Package data into metered API endpoints and manage customer API credits.",
    wallet_budget: 150, daily_cap: 75, monthly_cap: 600 },
  { key: "growth-marketer", name: "Growth Marketer", role: "marketer", icon: Target, accent: "#38BDF8",
    tagline: "Launches paid ad campaigns to your landing pages, auto-pauses losers.",
    mission: "Launch and optimize paid ad campaigns that drive qualified leads to landing pages, pausing any ad above the cost-per-close target.",
    wallet_budget: 300, daily_cap: 150, monthly_cap: 1500 },
];

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [modal, setModal] = useState(null); // {type, agent, preset}
  const [deploying, setDeploying] = useState(null);
  const load = () => api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 12000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => load());

  const deployTemplate = async (tpl) => {
    setDeploying(tpl.key);
    try {
      await api.post("/agents", {
        name: tpl.name, role: tpl.role, mission: tpl.mission,
        wallet_budget: tpl.wallet_budget, daily_cap: tpl.daily_cap, monthly_cap: tpl.monthly_cap,
        max_children: 3, max_child_depth: 2,
      });
      toast.success(`${tpl.name} deployed`, { description: tpl.tagline });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to deploy template";
      if (String(detail).toLowerCase().includes("treasury")) {
        toast.warning("Fund your treasury first", { description: "Lower the wallet budget or simulate a sale, then deploy." });
        setModal({ type: "create", preset: tpl });
      } else {
        toast.error(detail);
      }
    } finally { setDeploying(null); }
  };

  const control = async (agent, action) => {
    try {
      await api.post(`/agents/${agent.id}/control`, { action });
      toast.success(`${agent.name} → ${action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const toggleAutopilot = async (agent) => {
    try {
      await api.post(`/agents/${agent.id}/autopilot`, { enabled: !agent.autopilot });
      toast[agent.autopilot ? "message" : "success"](agent.autopilot ? `Autopilot OFF · ${agent.name}` : `Autopilot ON · ${agent.name}`, { description: agent.autopilot ? undefined : "Agent will act on schedule within policy" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const runAutopilot = async (agent) => {
    try {
      const { data } = await api.post(`/agents/${agent.id}/autopilot/run`);
      toast.success(`${agent.name} autopilot: ${data.action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Agents Registry & Spawn Factory</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Agents Fleet</h1>
        </div>
        <button
          data-testid="create-agent-btn"
          onClick={() => setModal({ type: "create" })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors"
        >
          <Plus className="w-4 h-4" /> Spawn New Agent
        </button>
      </div>

      {agents.length === 0 ? (
        <div data-testid="agents-empty-state">
          <div className="hud-card rounded-lg p-8 text-center">
            <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto">
              <Sparkles className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="font-display text-xl font-extrabold mt-4">Deploy your first agent in one click</h2>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">Pick a battle-tested template below — each ships with a role, mission, and sensible spend caps. Tune anything later, or build a fully custom agent.</p>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {TEMPLATES.map((t) => (
              <TemplateCard key={t.key} tpl={t} onDeploy={() => deployTemplate(t)} loading={deploying === t.key} />
            ))}
            <button
              data-testid="build-custom-agent-btn"
              onClick={() => setModal({ type: "create" })}
              className="hud-card rounded-lg p-5 flex flex-col items-center justify-center text-center border-dashed border-slate-700 hover:border-cyan-500/50 transition-colors min-h-[180px]"
            >
              <Plus className="w-6 h-6 text-slate-500" />
              <span className="mt-2 font-display font-bold text-sm">Build custom</span>
              <span className="text-[11px] text-slate-500 mt-1">Full control over role, mission &amp; caps</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="hud-card rounded-lg p-4" data-testid="quick-deploy-strip">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Quick-deploy a proven template</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`quick-template-${t.key}`}
                  onClick={() => deployTemplate(t)}
                  disabled={deploying === t.key}
                  className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 bg-[#080B11] hover:border-cyan-500/50 transition-colors disabled:opacity-50"
                  title={t.tagline}
                >
                  {deploying === t.key ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" /> : <t.icon className="w-3.5 h-3.5" style={{ color: t.accent }} />}
                  <span className="text-xs font-medium text-slate-200">{t.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((a) => {
          const rm = ROLE_META[a.role] || ROLE_META.builder;
          const accent = a.status === "active" ? "#10B981" : a.status === "frozen" ? "#EF4444" : a.status === "paused" ? "#F59E0B" : "#334155";
          const capPct = Math.min(100, (a.spent_today / (a.daily_cap || 1)) * 100);
          return (
            <div key={a.id} className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": accent }} data-testid={`agent-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-md bg-slate-800/60 border border-slate-700 flex items-center justify-center">
                    <rm.icon className={`w-4 h-4 ${rm.color}`} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm flex items-center gap-2">
                      {a.name}
                      {a.parent_id && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">CHILD · D{a.depth}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">{rm.label}</div>
                  </div>
                </div>
                <StatusBadge status={a.status} testid={`agent-status-${a.id}`} />
              </div>

              <p className="mt-3 text-xs text-slate-400 line-clamp-2 min-h-[2rem]">{a.mission}</p>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <Metric label="Wallet" value={money(a.wallet_balance)} accent="text-emerald-400" />
                <Metric label="Revenue" value={money(a.revenue_generated)} accent="text-cyan-400" />
              </div>

              <div className="mt-3">
                <div className="flex justify-between text-[10px] font-mono text-slate-500 mb-1">
                  <span>DAILY SPEND {money(a.spent_today)}</span>
                  <span>CAP {money(a.daily_cap)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${capPct}%`, backgroundColor: capPct > 80 ? "#EF4444" : "#06B6D4" }} />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between rounded-md border border-slate-800 bg-[#080B11] px-3 py-2">
                <div className="flex items-center gap-2">
                  <Zap className={`w-3.5 h-3.5 ${a.autopilot ? "text-emerald-400" : "text-slate-600"}`} />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Autopilot</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {a.autopilot && a.status === "active" && (
                    <button data-testid={`autopilot-run-${a.id}`} onClick={() => runAutopilot(a)} title="Run one tick now"
                      className="w-7 h-7 rounded border border-cyan-500/30 text-cyan-400 flex items-center justify-center hover:bg-cyan-500/15 transition-colors">
                      <Gauge className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button data-testid={`autopilot-toggle-${a.id}`} onClick={() => toggleAutopilot(a)} disabled={a.status !== "active" && !a.autopilot}
                    className={`relative w-10 h-5 rounded-full transition-colors disabled:opacity-40 ${a.autopilot ? "bg-emerald-500" : "bg-slate-700"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${a.autopilot ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                <IconBtn testid={`spend-btn-${a.id}`} onClick={() => setModal({ type: "spend", agent: a })} icon={DollarSign} title="Request spend" />
                <IconBtn testid={`spawn-btn-${a.id}`} onClick={() => setModal({ type: "spawn", agent: a })} icon={GitFork} title="Spawn child" disabled={a.status !== "active"} />
                {a.status === "active" && <IconBtn testid={`pause-btn-${a.id}`} onClick={() => control(a, "pause")} icon={Pause} title="Pause" />}
                {(a.status === "paused" || a.status === "frozen") && <IconBtn testid={`resume-btn-${a.id}`} onClick={() => control(a, "resume")} icon={Play} title="Resume" />}
                {a.status === "active" && <IconBtn testid={`freeze-btn-${a.id}`} onClick={() => control(a, "freeze")} icon={Snowflake} title="Freeze" danger />}
                {a.status !== "killed" && <IconBtn testid={`kill-btn-${a.id}`} onClick={() => control(a, "kill")} icon={Skull} title="Kill" danger />}
              </div>
            </div>
          );
        })}
      </div>
        </>
      )}

      {modal?.type === "create" && <CreateAgentModal preset={modal.preset} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.type === "spawn" && <SpawnModal agent={modal.agent} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.type === "spend" && <SpendModal agent={modal.agent} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

function TemplateCard({ tpl, onDeploy, loading }) {
  const rm = ROLE_META[tpl.role];
  return (
    <div className="hud-card rounded-lg p-5 flex flex-col" data-testid={`template-card-${tpl.key}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-md bg-slate-800/60 border border-slate-700 flex items-center justify-center">
          <tpl.icon className="w-5 h-5" style={{ color: tpl.accent }} />
        </div>
        <span className={`font-mono text-[9px] uppercase tracking-wider ${rm.color}`}>{rm.label}</span>
      </div>
      <h3 className="font-display font-bold text-base mt-3">{tpl.name}</h3>
      <p className="text-xs text-slate-400 mt-1 flex-1 min-h-[2.5rem]">{tpl.tagline}</p>
      <div className="flex items-center gap-3 mt-3 text-[10px] font-mono text-slate-500">
        <span>Wallet {money(tpl.wallet_budget)}</span>
        <span>·</span>
        <span>Daily cap {money(tpl.daily_cap)}</span>
      </div>
      <button
        data-testid={`template-deploy-${tpl.key}`}
        onClick={onDeploy}
        disabled={loading}
        className="mt-4 flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Deploy {tpl.name} <ArrowRight className="w-3.5 h-3.5" /></>}
      </button>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-md border border-slate-800 bg-[#080B11] p-2.5">
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono font-bold tabular-nums text-sm mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, testid, danger, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "border-red-500/30 text-red-400 hover:bg-red-500/15" : "border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md hud-card rounded-xl p-6 bg-[#0E131F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">{label}</label>
      <input data-testid={testid} {...props} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
    </div>
  );
}

function Select({ label, testid, options, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">{label}</label>
      <select data-testid={testid} {...props} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Submit({ loading, children, testid }) {
  return (
    <button type="submit" data-testid={testid} disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors disabled:opacity-60">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
    </button>
  );
}

function CreateAgentModal({ onClose, onDone, preset }) {
  const [f, setF] = useState(preset
    ? { name: preset.name, role: preset.role, mission: preset.mission, wallet_budget: preset.wallet_budget, daily_cap: preset.daily_cap, monthly_cap: preset.monthly_cap, max_children: 3, max_child_depth: 2 }
    : { name: "", role: "builder", mission: "", wallet_budget: 500, daily_cap: 300, monthly_cap: 2000, max_children: 3, max_child_depth: 2 });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/agents", {
        ...f,
        wallet_budget: Number(f.wallet_budget), daily_cap: Number(f.daily_cap), monthly_cap: Number(f.monthly_cap),
        max_children: Number(f.max_children), max_child_depth: Number(f.max_child_depth),
      });
      toast.success(`Agent "${f.name}" deployed`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create agent");
    } finally { setLoading(false); }
  };
  return (
    <Modal title="Spawn New Agent" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Agent Name" testid="agent-name-input" value={f.name} onChange={set("name")} required placeholder="Atlas-Builder" />
        <Select label="Role" testid="agent-role-select" value={f.role} onChange={set("role")} options={[
          { value: "builder", label: "Builder — sites, APIs, products" },
          { value: "seller", label: "Seller — outreach & closing" },
          { value: "support", label: "Support — customer care" },
          { value: "api_operator", label: "API Operator — data services" },
          { value: "marketer", label: "Marketer — paid ad campaigns" },
        ]} />
        <div>
          <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Mission</label>
          <textarea data-testid="agent-mission-input" value={f.mission} onChange={set("mission")} required rows={2} placeholder="Build & deploy landing pages for coffee brands"
            className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input label="Wallet $" type="number" testid="agent-budget-input" value={f.wallet_budget} onChange={set("wallet_budget")} />
          <Input label="Daily Cap" type="number" testid="agent-daily-input" value={f.daily_cap} onChange={set("daily_cap")} />
          <Input label="Monthly" type="number" testid="agent-monthly-input" value={f.monthly_cap} onChange={set("monthly_cap")} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Max Children" type="number" testid="agent-maxchildren-input" value={f.max_children} onChange={set("max_children")} />
          <Input label="Max Depth" type="number" testid="agent-maxdepth-input" value={f.max_child_depth} onChange={set("max_child_depth")} />
        </div>
        <Submit loading={loading} testid="agent-submit-btn">Deploy Agent</Submit>
      </form>
    </Modal>
  );
}

function SpawnModal({ agent, onClose, onDone }) {
  const [f, setF] = useState({ name: "", role: agent.role, mission: "", wallet_budget: 100, daily_cap: 50, monthly_cap: 500 });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/agents/${agent.id}/spawn`, { ...f, wallet_budget: Number(f.wallet_budget), daily_cap: Number(f.daily_cap), monthly_cap: Number(f.monthly_cap) });
      toast.success(`Child "${f.name}" spawned from ${agent.name}`, { description: "Inherited policy & allowed actions" });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Spawn failed");
    } finally { setLoading(false); }
  };
  return (
    <Modal title={`Spawn Child of ${agent.name}`} onClose={onClose}>
      <div className="mb-3 text-xs text-slate-400 bg-cyan-500/5 border border-cyan-500/20 rounded-md p-3">
        Parent wallet: <span className="font-mono text-emerald-400">{money(agent.wallet_balance)}</span> · Depth <span className="font-mono text-cyan-400">{agent.depth}/{agent.max_child_depth}</span> · Slots <span className="font-mono text-cyan-400">{agent.child_count}/{agent.max_children}</span>. Child inherits allowed actions.
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Child Name" testid="child-name-input" value={f.name} onChange={set("name")} required placeholder="Scout-Outreach" />
        <Select label="Role" testid="child-role-select" value={f.role} onChange={set("role")} options={[
          { value: "builder", label: "Builder" }, { value: "seller", label: "Seller" },
          { value: "support", label: "Support" }, { value: "api_operator", label: "API Operator" },
          { value: "marketer", label: "Marketer" },
        ]} />
        <Input label="Mission" testid="child-mission-input" value={f.mission} onChange={set("mission")} required placeholder="Qualify leads for parent" />
        <div className="grid grid-cols-3 gap-2">
          <Input label="Budget $" type="number" testid="child-budget-input" value={f.wallet_budget} onChange={set("wallet_budget")} />
          <Input label="Daily" type="number" testid="child-daily-input" value={f.daily_cap} onChange={set("daily_cap")} />
          <Input label="Monthly" type="number" testid="child-monthly-input" value={f.monthly_cap} onChange={set("monthly_cap")} />
        </div>
        <Submit loading={loading} testid="child-submit-btn">Spawn Child Agent</Submit>
      </form>
    </Modal>
  );
}

function SpendModal({ agent, onClose, onDone }) {
  const [f, setF] = useState({ amount: 50, category: "content_generation", description: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/spends", { agent_id: agent.id, amount: Number(f.amount), category: f.category, description: f.description || f.category, idempotency_key: `${agent.id}-${Date.now()}` });
      if (data.status === "executed") toast.success(`Spend executed: ${money(f.amount)}`, { description: data.reason });
      else if (data.status === "pending") toast.warning("Spend queued for approval", { description: data.reason });
      else toast.error("Spend blocked", { description: data.reason });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Spend failed");
    } finally { setLoading(false); }
  };
  return (
    <Modal title={`Request Spend — ${agent.name}`} onClose={onClose}>
      <div className="mb-3 text-xs text-slate-400 bg-amber-500/5 border border-amber-500/20 rounded-md p-3">
        Wallet <span className="font-mono text-emerald-400">{money(agent.wallet_balance)}</span>. Spends above the workspace approval threshold route to the HITL queue. Over-wallet spends trigger a hard-stop freeze.
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Amount ($)" type="number" testid="spend-amount-input" value={f.amount} onChange={set("amount")} required />
        <Select label="Category" testid="spend-category-select" value={f.category} onChange={set("category")} options={CATEGORIES.map((c) => ({ value: c, label: c.replace("_", " ") }))} />
        <Input label="Description" testid="spend-description-input" value={f.description} onChange={set("description")} placeholder="e.g. purchase coffeebrand.io domain" />
        <Submit loading={loading} testid="spend-submit-btn">Submit Spend Request</Submit>
      </form>
    </Modal>
  );
}
